import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, ClipboardCheck, PackageX } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { AdvancedStockReport } from '@/types'
import * as Help from '@/lib/inventory-report-help'
import { MetricHint, fmtQtyUnit, type CountTarget } from './primitives'

const REASON_COPY: Record<string, string> = {
  never_verified: 'Added by hand — never counted or purchased, so nothing confirms this quantity',
  stale_count: 'Not counted in a while',
  negative_stock: 'Shows less than zero — the recorded number is wrong',
}

/**
 * "What needs your attention?" — reorder pressure (linking to the Alerts tab,
 * which owns the item-level detail) and the count worklist: the items whose
 * recorded quantity most deserves a physical check, the NOOK-800kg class of
 * silent error first.
 */
export function NeedsAttention({ attention, onCountItem, onOpenAlerts }: {
  attention: AdvancedStockReport['attention']
  onCountItem: (item: CountTarget) => void
  onOpenAlerts: () => void
}) {
  const { formatCurrency } = useCurrency()
  const recs = attention.count_recommendations ?? []
  const reorderTotal = attention.low_stock_count + attention.out_of_stock_count
  const allQuiet = reorderTotal === 0 && attention.negative_stock_count === 0 && recs.length === 0

  return (
    <Card className={recs.length > 0 || reorderTotal > 0 ? 'border-warning-border' : undefined}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base font-semibold">What needs your attention?</CardTitle>
          <MetricHint text={Help.ATTENTION_INTRO} label="attention" />
        </div>
        <CardDescription className="text-xs">
          The short list to act on — reorder these, count those.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {allQuiet ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nothing needs your attention right now.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {attention.out_of_stock_count > 0 && (
                <Badge variant="destructive" className="text-xs">
                  <PackageX className="w-3.5 h-3.5 mr-1" /> {attention.out_of_stock_count} out of stock
                </Badge>
              )}
              {attention.low_stock_count > 0 && (
                <Badge variant="outline" className="text-xs border-warning-border text-warning-fg">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {attention.low_stock_count} running low
                </Badge>
              )}
              {(reorderTotal > 0) && (
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onOpenAlerts}>
                  Open reorder list
                </Button>
              )}
              <MetricHint text={Help.ATTENTION_LOW_STOCK} label="reorder list" />
            </div>

            {recs.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <h4 className="text-sm font-semibold">Count these items</h4>
                  <MetricHint text={Help.ATTENTION_COUNT_THESE} label="count these" />
                </div>
                <ul className="divide-y rounded-lg border">
                  {recs.map(r => (
                    <li key={r.item_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium truncate">{r.name}</span>
                          <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            {fmtQtyUnit(r.on_hand, r.unit)}{r.value > 0.005 ? ` · ${formatCurrency(r.value)}` : ''}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {REASON_COPY[r.reason] ?? r.reason}
                          {r.reason === 'stale_count' && r.days_since_count != null
                            ? ` — last counted ${Math.round(r.days_since_count)} days ago`
                            : ''}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 shrink-0"
                        onClick={() => onCountItem({
                          id: r.item_id,
                          name: r.name,
                          unit: r.unit,
                          quantity_on_hand: r.on_hand,
                          unit_cost: r.on_hand > 0 ? r.value / r.on_hand : undefined,
                        })}
                      >
                        <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Count now
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
